import {
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  Input,
  Spinner,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack
} from '@chakra-ui/react';
import { Bot, ChevronLeft, Edit3, MessageSquare, Mic, MicOff, MoreVertical, Phone, Plus, Send } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import type { ChatThread } from '../App';
import { useOpenAIRealtime, type ChatItem } from '../hooks';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai' | 'system';
  timestamp: Date;
}

interface ChatPageProps {
  integration: string;
  position: string;
  threadId: string;
  onBack: () => void;
  onUpdateThread: (threadId: string, updates: Partial<ChatThread>) => void;
  onNewChat: () => void;
}

export function ChatPage({ 
  integration, 
  position, 
  threadId, 
  onBack, 
  onUpdateThread,
  onNewChat 
}: ChatPageProps) {
  // Traditional messages state for backward compatibility
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Привет! Я ваш ИИ-помощник. Готов помочь с вопросами по работе. О чем хотели бы поговорить?',
      sender: 'ai',
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [chatTitle, setChatTitle] = useState('Новый чат');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // OpenAI Realtime integration
  const {
    connecting,
    connected,
    error,
    chat,
    connect,
    disconnect,
    sendText
  } = useOpenAIRealtime();

  // Merge traditional messages with OpenAI chat
  const allMessages: Message[] = [
    ...messages,
    ...chat.map((item: ChatItem) => ({
      id: item.id,
      text: item.text || JSON.stringify(item.data) || '',
      sender: item.role === 'tool' ? 'system' as const : 
              item.role === 'user' ? 'user' as const : 'ai' as const,
      timestamp: new Date()
    }))
  ];

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  const integrationNames: Record<string, string> = {
    salesforce: 'Salesforce',
    sap: 'SAP ERP',
    workday: 'Workday',
    hubspot: 'HubSpot',
    jira: 'Jira',
    servicenow: 'ServiceNow'
  };

  const positionNames: Record<string, string> = {
    'sales-manager': 'Менеджер по продажам',
    'sales-admin': 'Администратор Salesforce',
    'sap-consultant': 'SAP Консультант',
    'business-analyst': 'Бизнес-аналитик',
    'hr-manager': 'HR Менеджер',
    'payroll-specialist': 'Специалист по зарплате',
    'marketing-manager': 'Маркетинг менеджер',
    'project-manager': 'Проект менеджер',
    'it-admin': 'ИТ Администратор'
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [allMessages]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (connected) {
        disconnect();
      }
    };
  }, [connected, disconnect]);

  const sendMessage = () => {
    if (!inputText.trim()) return;

    // If connected to OpenAI Realtime, use that
    if (connected) {
      sendText(inputText);
      setInputText('');
      
      // Update thread info
      onUpdateThread(threadId, {
        lastMessage: inputText,
        timestamp: new Date(),
        messageCount: allMessages.length + 1,
        title: chatTitle === 'Новый чат' ? inputText.slice(0, 30) + '...' : chatTitle
      });
      
      return;
    }

    // Traditional message handling (fallback)
    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => {
      const newMessages = [...prev, userMessage];
      
      // Обновляем информацию о ветке чата
      onUpdateThread(threadId, {
        lastMessage: inputText,
        timestamp: new Date(),
        messageCount: newMessages.length,
        title: chatTitle === 'Новый чат' ? inputText.slice(0, 30) + '...' : chatTitle
      });

      return newMessages;
    });
    
    setInputText('');

    // Автоматически обновляем заголовок чата если это первое сообщение
    if (messages.length === 1 && chatTitle === 'Новый чат') {
      const newTitle = inputText.slice(0, 30) + (inputText.length > 30 ? '...' : '');
      setChatTitle(newTitle);
    }

    // Simulate AI response for traditional mode
    setTimeout(() => {
      const aiResponses = [
        'Понял ваш вопрос. Для решения этой задачи рекомендую следующий подход...',
        'Отличный вопрос! В системе это настраивается через модуль конфигурации.',
        'Да, я могу помочь с этим. Обычно такие задачи решаются через API.',
        'Это распространенная ситуация. Давайте пошагово разберем решение.',
        'Для этого есть несколько вариантов. Какой из них больше подходит вашей ситуации?'
      ];

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponses[Math.floor(Math.random() * aiResponses.length)],
        sender: 'ai',
        timestamp: new Date()
      };

      setMessages(prev => {
        const newMessages = [...prev, aiMessage];
        
        // Обновляем информацию о последнем сообщении от ИИ
        onUpdateThread(threadId, {
          lastMessage: aiMessage.text,
          timestamp: new Date(),
          messageCount: newMessages.length
        });

        return newMessages;
      });
    }, 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  const toggleRecording = () => {
    setIsRecording(!isRecording);
  };

  const toggleCall = () => {
    setIsCallActive(!isCallActive);
  };

  const handleTitleSave = () => {
    setIsEditingTitle(false);
    onUpdateThread(threadId, { title: chatTitle });
  };

  return (
    <Box h="100%" bg={bgColor} display="flex" flexDirection="column" overflow="hidden">
      {/* Header */}
      <Box bg={cardBg} shadow="sm" p={4} flexShrink={0}>
        <Box maxW="md" mx="auto">
          <HStack justify="space-between">
            <HStack minW={0} flex={1}>
              <Button variant="ghost" size="sm" onClick={onBack} mr={3} flexShrink={0}>
                <Icon as={ChevronLeft} w={5} h={5} />
              </Button>
              <HStack spacing={3} minW={0} flex={1}>
                <Avatar
                  size="sm"
                  bg="linear-gradient(135deg, #10b981, #2563eb)"
                  icon={<Icon as={Bot} color="white" />}
                  flexShrink={0}
                />
                <VStack align="start" minW={0} flex={1} spacing={0}>
                  <HStack spacing={2}>
                    {isEditingTitle ? (
                      <Input
                        value={chatTitle}
                        onChange={(e) => setChatTitle(e.target.value)}
                        onBlur={handleTitleSave}
                        onKeyPress={(e) => e.key === 'Enter' && handleTitleSave()}
                        fontSize="sm"
                        fontWeight="medium"
                        h={6}
                        px={1}
                        autoFocus
                      />
                    ) : (
                      <Text 
                        fontWeight="medium"
                        cursor="pointer"
                        _hover={{ color: "blue.500" }}
                        onClick={() => setIsEditingTitle(true)}
                        wordBreak="break-word"
                        whiteSpace="normal"
                        noOfLines={2}
                      >
                        {chatTitle}
                      </Text>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      h={6}
                      w={6}
                      minW={6}
                      p={0}
                      flexShrink={0}
                      onClick={() => setIsEditingTitle(true)}
                    >
                      <Icon as={Edit3} w={3} h={3} />
                    </Button>
                  </HStack>
                  <HStack spacing={2} mt={1}>
                    <Badge colorScheme="gray" fontSize="xs">{integrationNames[integration]}</Badge>
                    <Box w={2} h={2} bg="green.500" borderRadius="full" flexShrink={0} />
                    <Text fontSize="xs" color="gray.500" wordBreak="break-word" whiteSpace="normal">
                      {positionNames[position]}
                    </Text>
                  </HStack>
                </VStack>
              </HStack>
            </HStack>
            
            <HStack spacing={2} flexShrink={0}>
              {/* AI Voice Toggle */}
              {!connected ? (
                <Button
                  colorScheme="blue"
                  variant="ghost"
                  size="sm"
                  onClick={connect}
                  isLoading={connecting}
                  loadingText=""
                  title="Enable Voice Assistant"
                >
                  <Icon as={Bot} w={4} h={4} />
                </Button>
              ) : (
                <Button
                  colorScheme="blue"
                  variant="solid"
                  size="sm"
                  onClick={disconnect}
                  title="Disable Voice Assistant"
                >
                  <Icon as={Bot} w={4} h={4} />
                </Button>
              )}
              
              {/* Microphone Button */}
              <Button 
                colorScheme={isRecording ? "red" : "gray"}
                variant={isRecording ? "solid" : "ghost"}
                size="sm"
                onClick={toggleRecording}
                title={isRecording ? "Stop Recording" : "Start Recording"}
              >
                <Icon as={isRecording ? MicOff : Mic} w={4} h={4} />
              </Button>
              
              {/* Traditional Call Button */}
              <Button 
                colorScheme={isCallActive ? "red" : "gray"}
                variant={isCallActive ? "solid" : "ghost"}
                size="sm"
                onClick={toggleCall}
                title={isCallActive ? "End Call" : "Start Call"}
              >
                <Icon as={Phone} w={4} h={4} />
              </Button>
              
              <Button variant="ghost" size="sm" onClick={onOpen}>
                <Icon as={MoreVertical} w={4} h={4} />
              </Button>
            </HStack>
          </HStack>
        </Box>
      </Box>

      {/* Call Status */}
      {isCallActive && (
        <Box bg="green.600" color="white" p={3} flexShrink={0}>
          <VStack maxW="md" mx="auto" spacing={0}>
            <Text fontSize="sm">Голосовой звонок активен</Text>
            <Text fontSize="xs" opacity={0.8}>00:32</Text>
          </VStack>
        </Box>
      )}

      {/* Messages */}
      <Box flex={1} overflowY="auto" p={4} minH={0}>
        <VStack maxW="md" mx="auto" spacing={4} align="stretch">
          {allMessages.map((message) => (
            <Flex
              key={message.id}
              justify={message.sender === 'user' ? 'flex-end' : message.sender === 'system' ? 'center' : 'flex-start'}
            >
              <Box
                maxW={message.sender === 'system' ? '90%' : '80%'}
                px={4}
                py={2}
                borderRadius="lg"
                bg={message.sender === 'user' ? 'blue.500' : 
                   message.sender === 'system' ? 'orange.50' : 'gray.100'}
                color={message.sender === 'user' ? 'white' : 
                      message.sender === 'system' ? 'orange.800' : 'gray.800'}
                wordBreak="break-word"
                whiteSpace="pre-wrap"
                border={message.sender === 'system' ? '1px solid' : 'none'}
                borderColor={message.sender === 'system' ? 'orange.200' : 'transparent'}
              >
                {message.sender === 'system' && (
                  <HStack spacing={1} mb={1}>
                    <Icon as={Bot} w={3} h={3} />
                    <Text fontSize="xs" fontWeight="medium" opacity={0.8}>System</Text>
                  </HStack>
                )}
                <Text fontSize="sm" lineHeight="1.4">{message.text}</Text>
                <Text fontSize="xs" opacity={0.7} mt={1}>
                  {message.timestamp.toLocaleTimeString('ru-RU', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </Text>
              </Box>
            </Flex>
          ))}
          <div ref={messagesEndRef} />
        </VStack>
      </Box>

      {/* Input Area */}
      <Box bg={cardBg} p={4} flexShrink={0} boxShadow="0 -2px 10px rgba(0,0,0,0.1)">
        <VStack maxW="md" mx="auto" spacing={3}>
          <HStack spacing={2} w="full">
            <Box flex={1} position="relative">
              <Input
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Введите сообщение..."
                pr={12}
              />
              <Button
                variant="ghost"
                size="sm"
                position="absolute"
                right={1}
                top="50%"
                transform="translateY(-50%)"
                color={isRecording ? 'red.500' : 'gray.400'}
                onClick={toggleRecording}
              >
                <Icon as={Mic} w={4} h={4} />
              </Button>
            </Box>
            <Button onClick={sendMessage} isDisabled={!inputText.trim()} colorScheme="blue">
              <Icon as={Send} w={4} h={4} />
            </Button>
          </HStack>
          
          {/* Quick Actions */}
          <VStack spacing={2} w="full">
            <HStack spacing={2} overflowX="auto" w="full" pb={2}>
              <Button variant="outline" size="sm" fontSize="xs" whiteSpace="nowrap" flexShrink={0}>
                Помощь с настройками
              </Button>
              <Button variant="outline" size="sm" fontSize="xs" whiteSpace="nowrap" flexShrink={0}>
                Технические вопросы
              </Button>
              <Button variant="outline" size="sm" fontSize="xs" whiteSpace="nowrap" flexShrink={0}>
                Обучение
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                fontSize="xs"
                whiteSpace="nowrap"
                flexShrink={0}
                onClick={onNewChat}
                leftIcon={<Icon as={Plus} w={3} h={3} />}
              >
                Новый чат
              </Button>
            </HStack>
          </VStack>
        </VStack>
      </Box>

      {/* Drawer for Chat Management */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Управление чатами</DrawerHeader>
          
          <DrawerBody>
            <VStack spacing={4} align="stretch">
              <Button 
                justifyContent="flex-start"
                onClick={onNewChat}
                leftIcon={<Icon as={Plus} w={4} h={4} />}
              >
                Новая ветка чата
              </Button>
              
              <VStack spacing={2} align="start">
                <Text fontSize="sm" fontWeight="medium">Текущий чат:</Text>
                <Card w="full">
                  <CardBody p={3}>
                    <HStack spacing={2}>
                      <Icon as={MessageSquare} w={4} h={4} color="gray.500" />
                      <VStack align="start" minW={0} flex={1} spacing={0}>
                        <Text fontWeight="medium" fontSize="sm" wordBreak="break-word" noOfLines={2}>
                          {chatTitle}
                        </Text>
                        <Text fontSize="xs" color="gray.500">{allMessages.length} сообщений</Text>
                      </VStack>
                    </HStack>
                  </CardBody>
                </Card>
              </VStack>
              
              <VStack spacing={2} align="start">
                <Text fontSize="sm" fontWeight="medium">AI Assistant Status:</Text>
                <HStack spacing={2}>
                  <Badge colorScheme={connected ? "green" : "gray"} fontSize="xs">
                    {connected ? "Voice Mode Active" : "Text Mode"}
                  </Badge>
                  {connecting && <Spinner size="xs" />}
                </HStack>
                {error && (
                  <Alert status="error" size="sm" borderRadius="md">
                    <AlertIcon boxSize={3} />
                    <Text fontSize="xs">Connection failed</Text>
                  </Alert>
                )}
              </VStack>
              
              <VStack spacing={2} align="start">
                <Text fontSize="sm" fontWeight="medium">Специалист:</Text>
                <HStack spacing={2}>
                  <Avatar
                    size="sm"
                    bg="linear-gradient(135deg, #10b981, #2563eb)"
                    icon={<Icon as={Bot} w={4} h={4} color="white" />}
                  />
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" fontWeight="medium">{positionNames[position]}</Text>
                    <Text fontSize="xs" color="gray.500">{integrationNames[integration]}</Text>
                  </VStack>
                </HStack>
              </VStack>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}